"use client";

import { useDataFetchOrchestrator } from "@/hooks/use-data-fetch-orchestrator";
import { useUrlFilters } from "@/hooks/use-url-filters";
import { useSearchPageMapData } from "@/providers/search";
import { Bounds } from "@/providers/search/stores/map-store";
import { cn } from "@/utils/cn";
import { transformFilters } from "@/utils/search/transform-filters";
import { useMap } from "@vis.gl/react-google-maps";
import { ButtonHTMLAttributes } from "react";
import { baseClasses, unselectedClasses } from "..";

interface SearchAreaButtonOverlayProps extends ButtonHTMLAttributes<HTMLButtonElement> {
	isLoading: boolean;
	setSelectedIndex: (index: number) => void;
	onSearchAreaClick?: () => void;
}

export const SearchAreaButton = function SearchAreaButton({
	isLoading,
	onSearchAreaClick,
	setSelectedIndex: _setSelectedIndex,
	...props
}: SearchAreaButtonOverlayProps) {
	const mode = useSearchPageMapData(state => state.mode);
	const fetchMapData = useSearchPageMapData(state => state.fetchMapData);
	const fetchNearByProjects = useSearchPageMapData(state => state.fetchNearByProjects);
	const { orchestrateFetch } = useDataFetchOrchestrator({ mode, fetchMapData, fetchNearByProjects });
	// const [isClicked, setIsClicked] = useState(false);
	const { filters, setFilters } = useUrlFilters();
	const bounds = useSearchPageMapData(state => state.bounds);
	const map = useMap();

	const handleSearchAreaClick = () => {
		// Call the callback to set the flag in parent component
		onSearchAreaClick?.();

		if (!bounds) {
			return;
		}

		// Get current zoom level from map
		const currentZoom = map?.getZoom();

		const { filtersForGraphQL, newFilters } = transformFilters({
			filters,
			selectedLocationId: "custom",
			mapBounds: map?.getBounds()?.toJSON?.() as Bounds,
			mapZoom: currentZoom,
			from: "search-area",
		});

		setFilters(newFilters, { useReplaceState: true });
		orchestrateFetch({ filters: newFilters, filtersForGraphQL, map });
	};

	const handleKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === "Enter" || event.key === " ") {
			event.preventDefault();
			handleSearchAreaClick();
		}
	};

	return (
		<button
			onClick={handleSearchAreaClick}
			onKeyDown={handleKeyDown}
			disabled={isLoading}
			className={cn(baseClasses, unselectedClasses, "border-black/40 border-1")}
			aria-label="Search this area"
			{...(isLoading && { "aria-busy": "true" })}
			{...props}
		>
			{isLoading ? (
				<>
					<div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
					<span>Searching...</span>
				</>
			) : (
				<span>Search this area</span>
			)}
		</button>
	);
};
